import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormGroup,
  FormLabel,
  Tabs,
  Tab,
  Divider,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Analytics as AnalyticsIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Visibility as VisibilityIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface AlertConfiguration {
  id: string;
  role: string;
  initialDelayMinutes: number;
  escalationDelayMinutes: number;
  maxEscalations: number;
  communicationTypes: string[];
  escalationRecipients: string[];
  quietHoursStart: string;
  quietHoursEnd: string;
  isActive: boolean;
}

interface AlertLog {
  id: string;
  employeeId: string;
  scheduleId: string;
  alertType: string;
  communicationType: string;
  sentTo: string[];
  sentAt: string;
  status: string;
  errorMessage?: string;
  employee?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  schedule?: {
    date: string;
    startTime: string;
    endTime: string;
    campaign?: {
      name: string;
    };
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`alert-tabpanel-${index}`}
      aria-labelledby={`alert-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AlertSystemPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [editingConfig, setEditingConfig] = useState<AlertConfiguration | null>(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailContent, setSelectedEmailContent] = useState<any>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [logsPage, setLogsPage] = useState(1);
  const [logsLimit, setLogsLimit] = useState(25);

  // Check if user has permission to manage alert system
  const canManageAlerts = user?.role && ['administrator', 'super_user'].includes(user.role);
  
  // Check if user is administrator (for archive button)
  const isAdministrator = user?.role === 'administrator';

  // Fetch alert system status
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['alertSystemStatus'],
    queryFn: () => apiClient.alertSystem.getStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch alert configurations
  const { data: configurationsData, isLoading: configurationsLoading } = useQuery({
    queryKey: ['alertConfigurations'],
    queryFn: () => apiClient.alertConfiguration.getAll(),
    enabled: canManageAlerts,
  });

  // Fetch alert logs with pagination
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['alertLogs', logsPage, logsLimit],
    queryFn: () => apiClient.alertSystem.getLogs({ page: logsPage, limit: logsLimit }),
    enabled: canManageAlerts,
  });
  const [logsSortBy, setLogsSortBy] = useState<'employee'|'schedule'|'type'|'comm'|'status'|'sentAt'>('sentAt');
  const [logsSortDir, setLogsSortDir] = useState<'asc'|'desc'>('desc');

  // Use logs directly from API (server-side sorting is handled by API)
  const logs = logsData?.data?.data?.logs || [];
  
  const sortedLogs = React.useMemo(() => {
    const list: any[] = [...logs];
    list.sort((a,b)=>{
      let av:any=''; let bv:any='';
      switch (logsSortBy){
        case 'employee':
          av = `${a.employee?.lastName||''}${a.employee?.firstName||''}`.toLowerCase();
          bv = `${b.employee?.lastName||''}${b.employee?.firstName||''}`.toLowerCase();
          break;
        case 'schedule':
          av = a.schedule ? `${a.schedule.date} ${a.schedule.startTime}` : '';
          bv = b.schedule ? `${b.schedule.date} ${b.schedule.startTime}` : '';
          break;
        case 'type':
          av = a.alertType; bv = b.alertType; break;
        case 'comm':
          av = a.communicationType; bv = b.communicationType; break;
        case 'status':
          av = a.status; bv = b.status; break;
        case 'sentAt':
        default:
          av = new Date(a.sentAt).getTime();
          bv = new Date(b.sentAt).getTime();
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return logsSortDir==='asc' ? cmp : -cmp;
    });
    return list;
  }, [logs, logsSortBy, logsSortDir]);

  const toggleLogsSort = (col:'employee'|'schedule'|'type'|'comm'|'status'|'sentAt') => {
    if (logsSortBy===col) setLogsSortDir(d=>d==='asc'?'desc':'asc');
    else { setLogsSortBy(col); setLogsSortDir(col==='sentAt'?'desc':'asc'); }
  };

  // Fetch alert statistics
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['alertStatistics'],
    queryFn: () => apiClient.alertSystem.getStatistics(),
    enabled: canManageAlerts,
  });

  // Mutations
  const startServiceMutation = useMutation({
    mutationFn: () => apiClient.alertSystem.start(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertSystemStatus'] });
    },
  });

  const stopServiceMutation = useMutation({
    mutationFn: () => apiClient.alertSystem.stop(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertSystemStatus'] });
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: ({ role, config }: { role: string; config: any }) =>
      apiClient.alertConfiguration.update(role, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertConfigurations'] });
      setEditDialogOpen(false);
      setEditingConfig(null);
    },
  });

  // Archive logs mutation
  const archiveLogsMutation = useMutation({
    mutationFn: () => apiClient.alertSystem.clearLogs(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['alertLogs'] });
      queryClient.invalidateQueries({ queryKey: ['alertStatistics'] });
      const archivedCount = data.data.data?.archivedCount || 0;
      setMessage({ type: 'success', text: data.data.message || `Successfully archived ${archivedCount} alert log entries` });
      // Reset to first page after archiving
      setLogsPage(1);
    },
    onError: (error: any) => {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to archive alert logs' });
    },
  });

  const handleEditConfig = (role: string) => {
    const config = configurationsData?.data.data?.find((c: AlertConfiguration) => c.role === role);
    if (config) {
      setSelectedRole(role);
      // Parse JSON fields that might be strings
      const parsedConfig = {
        ...config,
        communicationTypes: typeof config.communicationTypes === 'string' 
          ? JSON.parse(config.communicationTypes) 
          : config.communicationTypes || [],
        escalationRecipients: typeof config.escalationRecipients === 'string'
          ? JSON.parse(config.escalationRecipients)
          : config.escalationRecipients || []
      };
      setEditingConfig(parsedConfig);
      setEditDialogOpen(true);
    }
  };

  const handleSaveConfig = () => {
    if (editingConfig) {
      updateConfigMutation.mutate({
        role: selectedRole,
        config: editingConfig,
      });
    }
  };

  const handleViewEmail = async (logId: string) => {
    try {
      const response = await apiClient.alertSystem.getEmailContent(logId);
      if (response.data.success) {
        setSelectedEmailContent(response.data.data);
        setEmailDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching email content:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'sent':
        return <Chip icon={<CheckCircleIcon />} label="Sent" color="success" size="small" />;
      case 'failed':
        return <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />;
      case 'delivered':
        return <Chip icon={<CheckCircleIcon />} label="Delivered" color="success" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const getCommunicationIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <EmailIcon fontSize="small" />;
      case 'sms':
        return <SmsIcon fontSize="small" />;
      case 'in_app':
        return <NotificationsActiveIcon fontSize="small" />;
      default:
        return <NotificationsIcon fontSize="small" />;
    }
  };

  if (!canManageAlerts) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to access the Alert System management.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Alert System Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure and monitor the missing employee alert system.
      </Typography>

      {/* Message Display */}
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* System Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">System Status</Typography>
            <Box>
              <IconButton
                onClick={() => queryClient.invalidateQueries({ queryKey: ['alertSystemStatus'] })}
                disabled={statusLoading}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
          
          {statusLoading ? (
            <CircularProgress size={24} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color={statusData?.data.data?.isEnabled ? 'success.main' : 'error.main'}>
                    {statusData?.data.data?.isEnabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Global Status
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color={statusData?.data.data?.serviceRunning ? 'success.main' : 'error.main'}>
                    {statusData?.data.data?.serviceRunning ? 'Running' : 'Stopped'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Service Status
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center">
                  <Typography variant="h6">
                    {new Date(statusData?.data.data?.lastChecked || '').toLocaleTimeString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last Checked
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          <Box mt={2} display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={() => startServiceMutation.mutate()}
              disabled={startServiceMutation.isPending || statusData?.data.data?.serviceRunning}
            >
              Start Service
            </Button>
            <Button
              variant="outlined"
              startIcon={<StopIcon />}
              onClick={() => stopServiceMutation.mutate()}
              disabled={stopServiceMutation.isPending || !statusData?.data.data?.serviceRunning}
            >
              Stop Service
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab icon={<HistoryIcon />} label="Alert Logs" />
          <Tab icon={<AnalyticsIcon />} label="Statistics" />
          <Tab icon={<SettingsIcon />} label="Configurations" />
        </Tabs>
      </Box>

      {/* Configurations Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom>
          Role-Based Alert Configurations
        </Typography>
        
        {configurationsLoading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={2}>
            {configurationsData?.data.data?.map((config: AlertConfiguration) => (
              <Grid item xs={12} md={6} key={config.role}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                        {config.role.replace('_', ' ')}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleEditConfig(config.role)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                    
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Initial Delay: {config.initialDelayMinutes} minutes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Escalation Delay: {config.escalationDelayMinutes} minutes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Max Escalations: {config.maxEscalations}
                      </Typography>
                    </Box>

                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Communication Types:
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {Array.isArray(config.communicationTypes) 
                          ? config.communicationTypes.map((type) => (
                              <Chip
                                key={type}
                                icon={getCommunicationIcon(type)}
                                label={type.replace('_', ' ')}
                                size="small"
                              />
                            ))
                          : <Typography variant="body2" color="text.secondary">No communication types configured</Typography>
                        }
                      </Box>
                    </Box>

                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Escalation Recipients:
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {Array.isArray(config.escalationRecipients) 
                          ? config.escalationRecipients.map((recipient) => (
                              <Chip
                                key={recipient}
                                label={recipient.replace('_', ' ')}
                                size="small"
                                variant="outlined"
                              />
                            ))
                          : <Typography variant="body2" color="text.secondary">No escalation recipients configured</Typography>
                        }
                      </Box>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Quiet Hours: {config.quietHoursStart} - {config.quietHoursEnd}
                      </Typography>
                      <Chip
                        label={config.isActive ? 'Active' : 'Inactive'}
                        color={config.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Alert Logs Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Recent Alert Logs
            {logsData?.data?.data?.total !== undefined && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({logsData.data.data.total} total)
              </Typography>
            )}
          </Typography>
          {isAdministrator ? (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ArchiveIcon />}
              onClick={() => archiveLogsMutation.mutate()}
              disabled={archiveLogsMutation.isPending}
            >
              {archiveLogsMutation.isPending ? 'Archiving...' : 'Archive All Logs'}
            </Button>
          ) : null}
        </Box>
        
        {logsLoading ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleLogsSort('employee')}>Employee {logsSortBy==='employee'?(logsSortDir==='asc'?'▲':'▼'):''}</TableCell>
                  <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleLogsSort('schedule')}>Schedule {logsSortBy==='schedule'?(logsSortDir==='asc'?'▲':'▼'):''}</TableCell>
                  <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleLogsSort('type')}>Alert Type {logsSortBy==='type'?(logsSortDir==='asc'?'▲':'▼'):''}</TableCell>
                  <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleLogsSort('comm')}>Communication {logsSortBy==='comm'?(logsSortDir==='asc'?'▲':'▼'):''}</TableCell>
                  <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleLogsSort('status')}>Status {logsSortBy==='status'?(logsSortDir==='asc'?'▲':'▼'):''}</TableCell>
                  <TableCell sx={{cursor:'pointer'}} onClick={()=>toggleLogsSort('sentAt')}>Sent At {logsSortBy==='sentAt'?(logsSortDir==='asc'?'▲':'▼'):''}</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLogs.map((log: AlertLog) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {log.employee ? `${log.employee.firstName} ${log.employee.lastName}` : 'Unknown'}
                    </TableCell>
                    <TableCell>
                      {log.schedule ? (
                        <Box>
                          <Typography variant="body2">
                            {log.schedule.date} {log.schedule.startTime} - {log.schedule.endTime}
                          </Typography>
                          {log.schedule.campaign && (
                            <Typography variant="caption" color="text.secondary">
                              {log.schedule.campaign.name}
                            </Typography>
                          )}
                        </Box>
                      ) : 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.alertType.replace('_', ' ')}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getCommunicationIcon(log.communicationType)}
                        <Typography variant="body2">
                          {log.communicationType.replace('_', ' ')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(log.status)}
                    </TableCell>
                    <TableCell>
                      {new Date(log.sentAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {log.communicationType === 'email' && (
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewEmail(log.id)}
                          variant="outlined"
                        >
                          View Email
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {logsData?.data?.data && logsData.data.data.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Per Page</InputLabel>
              <Select
                value={logsLimit}
                onChange={(e) => {
                  setLogsLimit(Number(e.target.value));
                  setLogsPage(1);
                }}
                label="Per Page"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Pagination
              count={logsData.data.data.totalPages || 0}
              page={logsPage}
              onChange={(_, newPage) => setLogsPage(newPage)}
              color="primary"
              showFirstButton
              showLastButton
            />
            <Typography variant="body2" color="text.secondary">
              Showing {((logsPage - 1) * logsLimit) + 1} - {Math.min(logsPage * logsLimit, logsData.data.data.total || 0)} of {logsData.data.data.total || 0}
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Statistics Tab */}
      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" gutterBottom>
          Alert Statistics
        </Typography>
        
        {statsLoading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="primary">
                    {statsData?.data.data?.totalAlerts || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Alerts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="success.main">
                    {statsData?.data.data?.recentAlerts || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recent (24h)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="error.main">
                    {statsData?.data.data?.alertsByStatus?.find((s: any) => s.status === 'failed')?.count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Failed Alerts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4" color="info.main">
                    {statsData?.data.data?.alertsByStatus?.find((s: any) => s.status === 'sent')?.count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sent Alerts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Edit Configuration Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Alert Configuration</DialogTitle>
        <DialogContent>
          {editingConfig && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Initial Delay (minutes)"
                    type="number"
                    value={editingConfig.initialDelayMinutes}
                    onChange={(e) =>
                      setEditingConfig({
                        ...editingConfig,
                        initialDelayMinutes: parseInt(e.target.value),
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Escalation Delay (minutes)"
                    type="number"
                    value={editingConfig.escalationDelayMinutes}
                    onChange={(e) =>
                      setEditingConfig({
                        ...editingConfig,
                        escalationDelayMinutes: parseInt(e.target.value),
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Max Escalations"
                    type="number"
                    value={editingConfig.maxEscalations}
                    onChange={(e) =>
                      setEditingConfig({
                        ...editingConfig,
                        maxEscalations: parseInt(e.target.value),
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingConfig.isActive}
                        onChange={(e) =>
                          setEditingConfig({
                            ...editingConfig,
                            isActive: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Quiet Hours Start"
                    value={editingConfig.quietHoursStart}
                    onChange={(e) =>
                      setEditingConfig({
                        ...editingConfig,
                        quietHoursStart: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Quiet Hours End"
                    value={editingConfig.quietHoursEnd}
                    onChange={(e) =>
                      setEditingConfig({
                        ...editingConfig,
                        quietHoursEnd: e.target.value,
                      })
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormLabel component="legend">Communication Types</FormLabel>
                  <FormGroup>
                    {['email', 'sms', 'in_app'].map((type) => (
                      <FormControlLabel
                        key={type}
                        control={
                        <Checkbox
                          checked={(editingConfig.communicationTypes || []).includes(type)}
                          onChange={(e) => {
                            const currentTypes = Array.isArray(editingConfig.communicationTypes) 
                              ? editingConfig.communicationTypes 
                              : [];
                            const newTypes = e.target.checked
                              ? [...currentTypes, type]
                              : currentTypes.filter((t) => t !== type);
                            setEditingConfig({
                              ...editingConfig,
                              communicationTypes: newTypes,
                            });
                          }}
                        />
                        }
                        label={type.replace('_', ' ')}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveConfig}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={updateConfigMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Content Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon />
            Email Content
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEmailContent && (
            <Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Subject:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedEmailContent.subject}
                </Typography>
              </Box>
              
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Sent To:
                </Typography>
                <Typography variant="body2">
                  {Array.isArray(selectedEmailContent.sentTo) 
                    ? selectedEmailContent.sentTo.join(', ')
                    : selectedEmailContent.sentTo}
                </Typography>
              </Box>
              
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Sent At:
                </Typography>
                <Typography variant="body2">
                  {new Date(selectedEmailContent.sentAt).toLocaleString()}
                </Typography>
              </Box>
              
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status:
                </Typography>
                <Chip
                  label={selectedEmailContent.status}
                  color={selectedEmailContent.status === 'sent' ? 'success' : 'error'}
                  size="small"
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Email Content:
              </Typography>
              
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#f9f9f9',
                  maxHeight: '400px',
                  overflow: 'auto'
                }}
                dangerouslySetInnerHTML={{ __html: selectedEmailContent.html }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AlertSystemPage;
